import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";

import { useBranding } from "../../../BrandingContext";
import { useNavigation } from "../../../hooks/useNavigation";
import { tokens } from "../../../theme";
import Button from "../../common/Button";
import Card from "../../common/Card";
import CustomTextField from "../../common/CustomTextField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import Footer from "../../common/Footer";
import CustomNametField from "../../common/CustomNametField";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { validatePracticeDetails, validateNpi } from "../../../redux/slice/practiceSlice";
import CustomPasswordTextField from "../../common/CustomPasswordTextField";
import { useEffect } from "react";

// type contactNameInfo = {
//   firstName: string;
//   middleName: string;
//   lastName: string;
// }

type CreateAccountForm = {
  practiceName: string;
  npi: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
  confirmpassword: string;
};

type CreatePracticeAndUserRequest = {
  practiceName: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  npi: string;
  password?: string;
};

export default function CreateAccountStep() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const { navigate } = useNavigation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const createPracticeAndUserRequestObj = location.state?.createPracticeAndUserRequest;
  const { control, handleSubmit, watch, setValue } = useForm<CreateAccountForm>({
    defaultValues: {
      practiceName: createPracticeAndUserRequestObj?.practiceName ?? "",
      npi: createPracticeAndUserRequestObj?.npi ?? "",
      firstName: createPracticeAndUserRequestObj?.contactFirstName ?? "",
      lastName: createPracticeAndUserRequestObj?.contactLastName ?? "",
      phone: createPracticeAndUserRequestObj?.contactPhone ?? "",
      email: createPracticeAndUserRequestObj?.contactEmail ?? "",
      password: createPracticeAndUserRequestObj?.password ?? "",
      confirmpassword: createPracticeAndUserRequestObj?.password ?? "",
    },
  });
  const npiValue = watch("npi");

  const step = 1;
  const totalSteps = 3;
  const percent = Math.round((step / totalSteps) * 100);

  const onSubmit = async (data: CreateAccountForm) => {
    const createPracticeAndUserRequest: CreatePracticeAndUserRequest = {
      practiceName: data.practiceName,
      contactFirstName: data.firstName,
      contactLastName: data.lastName,
      contactEmail: data.email,
      contactPhone: data.phone,
      npi: data.npi,
      password: data.password
    }
    const validatedResponse = await dispatch(validatePracticeDetails(createPracticeAndUserRequest));
    if (validatedResponse.payload && validatedResponse.payload.code === "V3OK" && validatedResponse.payload.message === "SUCCESS") {
      navigate("/setup_two_factor", { state: { createPracticeAndUserRequest } });
    }
  };

  useEffect(() => {
    if (npiValue?.length === 10) {
      dispatch(validateNpi(npiValue)).then((res: any) => {
        if (res.payload?.code === "V3OK" && res.payload?.data?.length > 0) {
          const orgName = res.payload.data[0]?.basic?.organization_name;
          if (orgName) {
            setValue("practiceName", orgName, { shouldValidate: true });
          }
        }
      });
    }
  }, [npiValue]);

  const logoSrc =
    theme.palette.mode === "dark"
      ? branding?.logoWhite
      : branding?.logo || require("../../../branding-assets/v3biomed/logo.png");

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pb: 4,
          mt: isMobile ? 8 : 0,
        }}
      >
        <Container maxWidth="sm">
          <Card>
            <Box display="flex" justifyContent="center" mb={3}>
              <Box
                component="img"
                src={logoSrc}
                alt="V3 Biomed"
                sx={{
                  height: 64,
                  maxWidth: "70%",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Box px={{ xs: 2, sm: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography fontWeight={700} color={colors.grey[300]}>
                  Step {step} of {totalSteps}
                </Typography>
                <Typography fontWeight={700} color={colors.grey[300]}>
                  {percent}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  mb: 4,
                  backgroundColor: colors.grey[900],
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    background:
                      "linear-gradient(90deg, #19C1A3, #14B8B5, #19A9FF)",
                  },
                }}
              />
              <Typography variant="h4" fontWeight={600} textAlign="center">
                Welcome to V3 Biomed!
              </Typography>

              <Typography
                mt={1}
                mb={4}
                textAlign="center"
                color={colors.grey[300]}
              >
                To access our products and all features, please complete your registration.
              </Typography>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      NPI
                    </Typography>
                    <CustomTextField
                      size="small"
                      placeholder="10-digit NPI number"
                      controllerName="npi"
                      control={control}
                      inputProps={{ inputMode: "numeric"}}
                      maxLength={10}
                      fullWidth
                      rules={{
                        required: "NPI is required.",
                        validate: {
                          doesNotContainSpaces: (value: string) =>
                            !/\s/.test(value) || `NPI cannot contain spaces.`,
                          customValidation: (value: string) => {
                            if (value?.trim() === "") {
                              return "NPI is required.";
                            }
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Practice Name
                    </Typography>
                    <CustomTextField
                      size="small"
                      placeholder="Enter your practice name"
                      controllerName="practiceName"
                      control={control}
                      fullWidth
                      rules={{
                        required: "Practice Name is required.",
                        validate: {
                          doesNotContainSpaces: (value: string) =>
                            !/^\s/.test(value) || "Practice Name cannot start with spaces.",
                          doesNotContainTrailingSpaces: (value: string) =>
                            !/\s$/.test(value) || "Practice Name cannot end with spaces.",
                          customValidation: (value: string) => {
                            if (value?.trim() === "") {
                              return "Practice Name is required.";
                            }
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      First Name
                    </Typography>
                    <CustomTextField
                      size="small"
                      placeholder="Enter your first name"
                      controllerName="firstName"
                      control={control}
                      rules={{
                        required: "First name is required.",
                        validate: {
                          doesNotContainSpaces: (value: string) =>
                            !/\s/.test(value) || "First name cannot contain spaces.",
                        },
                      }}
                      fullWidth
                    />
                    {/* <CustomNametField
                      size="small"
                      control={control}
                      name="contactInfo"
                      isRequired={true}
                      usePlaceholder={true}
                      hideMiddleName={true}
                    /> */}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Last Name
                    </Typography>
                    <CustomTextField
                      size="small"
                      placeholder="Enter your last name"
                      controllerName="lastName"
                      control={control}
                      rules={{
                        required: "Last name is required.",
                        validate: {
                          doesNotContainSpaces: (value: string) =>
                            !/\s/.test(value) || "Last name cannot contain spaces.",
                        },
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Phone
                    </Typography>
                    <CustomPatternTextField
                      size="small"
                      placeholder="(555) 555-5555"
                      control={control}
                      name="phone"
                      label="Phone"
                      rules={{
                        required: "Phone is required.",
                        pattern: {
                          value: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                          message: "Enter valid Phone Number."
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Email
                    </Typography>
                    <CustomTextField
                      size="small"
                      placeholder="contact@practice.com"
                      controllerName="email"
                      control={control}
                      inputProps={{ inputMode: "email" }}
                      fullWidth
                      rules={{
                        required: "Email is required.",
                        pattern: {
                          value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                          message: "Invalid email.",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Password
                    </Typography>
                    <CustomPasswordTextField
                      size="small"
                      placeholder="Create a password"
                      controllerName="password"
                      control={control}
                      fullWidth
                      rules={{
                        required: "Password is required.",
                        minLength: {
                          value: 12,
                          message: "Password should be at least 12 characters.",
                        },
                        pattern: {
                          value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?!.*\s)/,
                          message: "Password must contain at least one uppercase letter, one number, and one special character with no spaces"
                        },
                      }
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 600, color: colors.grey[200] }}
                    >
                      Confirm Password
                    </Typography>
                    <CustomPasswordTextField
                      size="small"
                      placeholder="Confirm password"
                      controllerName="confirmpassword"
                      control={control}
                      fullWidth
                      rules={{
                        required: "Confirmation of password is required.",
                        validate: (value: string) =>
                          value === watch("password") ||
                          "Passwords do not match",
                      }
                      }
                    />
                  </Grid>

                  <Grid item xs={12} mt={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: 1.5,
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#169c9c" : "#c6ebeb",
                        color: colors.grey[100],
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#169c9ce6"
                              : "#b6e0e0",
                        },
                      }}
                    >
                      Continue
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Box textAlign="center">
                      <Typography
                        sx={{
                          cursor: "pointer",
                          color: colors.grey[300],
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => navigate("/signin")}
                      >
                        Back to Sign In
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Card>
        </Container>
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: colors.grey[900],
          py: 1,
          textAlign: "center",
        }}
      >
        <Footer />
      </Box>
    </>
  );
}
